import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/AuthContext";
import { partnerBusinessRepo } from "@/data/repos/partnerBusinessRepo";

/** `error` is surfaced separately from `hasBusiness` because a failed request
 *  answers neither "has" nor "has not" — folded into `hasBusiness: false` it
 *  invites a partner to found the podnik they have been running for a year.
 *  Consumers must branch on `error` before they read `hasBusiness`.
 *
 *  A query still waiting for the signed-in user counts as loading for the same
 *  reason: react-query reports a disabled query as `isPending` without
 *  `isFetching`, which reads as a settled "no business".
 *
 *  A refetch that fails on top of an answer already given is not reported: the
 *  cached answer — a business or a proven absence of one — still says which
 *  screen the partner belongs on. */
export function useUserBusiness() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["user-business", user?.id],
    queryFn: () => partnerBusinessRepo.get(),
    enabled: !!user?.id,
  });

  const business = query.data ?? null;
  const userBusiness =
    business && user ? { userId: user.id, businessId: business.id, business } : null;

  const wasAnsweredOnce = query.data !== undefined;

  return {
    userBusiness,
    business,
    businessId: business?.id ?? null,
    isLoading: !user?.id || query.isLoading,
    error: wasAnsweredOnce ? null : query.error,
    refetch: () => void query.refetch(),
    hasBusiness: !!business,
  };
}
